/**
 * 데이터 테이블의 특정 컬럼에 마우스를 올렸을 때 설명을 풍선글로 보여주는 디렉티브.
 *
 * 목록이 수천 행까지 늘어날 수 있어서 셀마다 <v-tooltip>을 매다는 방식은 쓰지 않는다.
 * v-tooltip은 행 하나마다 Vue 컴포넌트 인스턴스가 생기므로 행 수에 비례해 무거워진다.
 * 여기서는 테이블 전체에 리스너 하나만 걸어 이벤트 위임으로 처리하고,
 * 풍선글 DOM도 문서 전체에 하나만 만들어 돌려쓴다. 행이 몇 개든 비용이 같다.
 *
 * 표시할 셀은 마크업이 아니라 헤더 이름으로 지정한다. 그래서 <template #item.xxx>를
 * 따로 만들 필요가 없고, 테이블 본문 마크업은 손대지 않아도 된다.
 *
 * 사용법:
 *   <div v-desc-tooltip="{ headers, texts, columns: { tcode: 'tcode', report: 'program' } }">
 *     <v-data-table :headers="headers" ... />
 *   </div>
 *
 *   headers : 데이터 테이블에 넘긴 headers 배열 그대로. td 위치로 컬럼을 찾는 데 쓴다.
 *   texts   : { 종류: { 코드: '설명' } } 모양의 사전. 페이지를 더 읽어올 때마다 채워 넣으면 된다.
 *   columns : { 헤더의 value: texts의 종류 }. 여기 없는 컬럼은 풍선글을 띄우지 않는다.
 *
 * Added.by.KWShin.2026.09.09.
 */

// 마우스를 올린 셀 위쪽에 띄운다. 셀과 풍선글 사이의 간격(px).
const GAP = 6;
// 화면 가장자리에서 이만큼은 떨어뜨린다.
const EDGE = 4;

// 문서 전체에서 하나만 쓰는 풍선글 요소.
let tipEl = null;

function getTipElement() {
  if (tipEl) return tipEl;
  tipEl = document.createElement('div');
  tipEl.className = 'desc-tooltip';
  tipEl.setAttribute('role', 'tooltip');
  document.body.appendChild(tipEl);
  return tipEl;
}

function hide() {
  if (tipEl) tipEl.style.display = 'none';
}

/**
 * 셀 위쪽 가운데에 맞춰 띄운다. 화면 밖으로 나가면 안쪽으로 밀어 넣고,
 * 위쪽 공간이 모자라면 셀 아래로 내린다.
 */
function showAbove(cell, text) {
  const el = getTipElement();
  el.textContent = text;
  el.style.display = 'block';
  // 크기를 재기 위해 먼저 보이게 한 뒤 위치를 잡는다.
  el.style.left = '0px';
  el.style.top = '0px';

  const cellRect = cell.getBoundingClientRect();
  const tipRect = el.getBoundingClientRect();

  let left = cellRect.left + cellRect.width / 2 - tipRect.width / 2;
  left = Math.max(EDGE, Math.min(left, window.innerWidth - tipRect.width - EDGE));

  let top = cellRect.top - tipRect.height - GAP;
  if (top < EDGE) top = cellRect.bottom + GAP;

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
}

/**
 * 마우스가 올라간 지점이 설명을 붙일 셀인지 보고, 맞으면 설명을 찾아 돌려준다.
 */
function resolve(binding, target) {
  const options = binding.value;
  if (!options || !options.headers || !options.columns || !options.texts) return null;

  const cell = target.closest ? target.closest('td') : null;
  if (!cell || !cell.parentNode) return null;

  const index = Array.prototype.indexOf.call(cell.parentNode.children, cell);
  const header = options.headers[index];
  if (!header) return null;

  const kind = options.columns[header.value];
  if (!kind) return null;

  const code = (cell.textContent || '').trim();
  if (!code) return null;

  const dictionary = options.texts[kind];
  if (!dictionary) return null;

  let text = dictionary[code];
  /*
   * SSR 목록의 Report 칸처럼 'ZPGM (잡이름)' 모양으로 뒤에 덧붙은 이름을 함께 보여주는 셀이 있다.
   * 사전의 열쇠는 앞의 코드뿐이므로, 보이는 글자 그대로 찾지 못하면 앞 조각으로 한 번 더 찾아본다.
   * Added.by.KWShin.2026.09.22.
   */
  if (!text) {
    const at = code.indexOf(' (');
    if (at > 0) text = dictionary[code.substring(0, at)];
  }
  if (!text) return null;

  return { cell, text };
}

export default {
  install(Vue) {
    Vue.directive('desc-tooltip', {
      bind(el, binding) {
        const handlers = {
          over(event) {
            const found = resolve(el._descTooltipBinding, event.target);
            if (found) showAbove(found.cell, found.text);
            else hide();
          },
          leave() {
            hide();
          },
        };

        el._descTooltipBinding = binding;
        el._descTooltipHandlers = handlers;

        // mouseover 는 버블링되므로 테이블 전체에 하나만 걸면 된다.
        el.addEventListener('mouseover', handlers.over);
        el.addEventListener('mouseleave', handlers.leave);
        // 스크롤이나 창 크기 변경으로 셀이 움직이면 위치가 어긋나므로 그냥 감춘다.
        el.addEventListener('scroll', handlers.leave, true);
        window.addEventListener('scroll', handlers.leave, true);
        window.addEventListener('resize', handlers.leave);
      },
      update(el, binding) {
        // 페이지를 더 읽어오면 texts 가 바뀌므로 최신 값을 물고 있게 한다.
        el._descTooltipBinding = binding;
      },
      unbind(el) {
        const handlers = el._descTooltipHandlers;
        if (handlers) {
          el.removeEventListener('mouseover', handlers.over);
          el.removeEventListener('mouseleave', handlers.leave);
          el.removeEventListener('scroll', handlers.leave, true);
          window.removeEventListener('scroll', handlers.leave, true);
          window.removeEventListener('resize', handlers.leave);
        }
        delete el._descTooltipBinding;
        delete el._descTooltipHandlers;
        hide();
      },
    });
  },
};
